import type { CalculatorArticleContent } from "../calculator-article";

export const wilksGuide: CalculatorArticleContent = {
  id: "wilks",
  eyebrow: "Powerlifting karşılaştırma rehberi",
  updatedAt: "29 Eylül 2026",
  articleTitle: "Wilks skorunu hesaplamak ve tarihsel bağlamıyla yorumlamak",
  intro:
    "Wilks, squat, bench press ve deadlift totalini vücut ağırlığına göre ölçekleyen tarihsel bir powerlifting puanlama sistemidir. Farklı sıkletleri karşılaştırmayı kolaylaştırır; fakat güncel IPF yarışmalarında IPF GL kullanılır. Bu nedenle skorun hangi formülle ve hangi amaçla hesaplandığını açıkça belirtmek gerekir.",
  sections: [
    {
      id: "wilks-nedir",
      title: "Wilks skoru nedir?",
      paragraphs: [
        "Mutlak total, ağır sıkletlerde doğal olarak daha yüksek olma eğilimindedir. Wilks formülü vücut ağırlığına ve cinsiyete bağlı bir katsayı üretir; bu katsayı powerlifting totaliyle çarpılır. Böylece farklı vücut ağırlıklarındaki sporcular tek sayı üzerinden karşılaştırılabilir.",
        "Formül squat, bench press ve deadlift'in aynı yarışmadaki en iyi geçerli sonuçlarının totalini kullanır. Yalnız bench press veya yalnız deadlift sonucunu aynı skorun üç kaldırış versiyonu gibi yorumlamak doğru değildir. Organizasyonun tek kaldırış için ayrı puanlama kuralı olabilir.",
        "Powerlifting Australia'nın yayımladığı açıklama Wilks katsayısının cinsiyete özgü polinomdan hesaplandığını gösterir. Hesaplayıcı bu katsayıyı kilogram cinsinden vücut ağırlığına uygular ve çıkan değerle totali çarpar.",
      ],
      sourceIds: ["1"],
    },
    {
      id: "girdiler",
      title: "Doğru Wilks hesabı için girdiler",
      paragraphs: [
        "Vücut ağırlığını ve totali aynı performans gününden kullan. Bir yarışmada 82,5 kg tartılıp 600 kg total yaptıysan girişler bunlardır. Bir yıl içindeki en düşük vücut ağırlığını en yüksek totalinle birleştirmek gerçek bir yarışma performansını temsil etmez.",
        "Pound cinsinden kayıtları önce kilograma dönüştür. Cinsiyet seçimi formülün katsayılarını değiştirir. Sonuçları paylaşırken 'Wilks' olduğunu ve varsa antrenman totalinden üretildiğini belirt; aksi durumda IPF GL veya resmî yarışma skoru sanılabilir.",
      ],
      table: {
        caption: "Wilks girdilerini kontrol et",
        headers: ["Alan", "Kullanılacak değer", "Yanlış örnek"],
        rows: [
          [
            "Vücut ağırlığı",
            "Performans günündeki kg",
            "Farklı dönemdeki en düşük kilo",
          ],
          [
            "Powerlifting totali",
            "Squat + Bench Press + Deadlift",
            "Yalnız bench press sonucu",
          ],
          ["Birim", "Kilogram", "Pound değerini doğrudan yazmak"],
          [
            "Bağlam",
            "Yarışma veya açıkça belirtilmiş antrenman",
            "E1RM totalini resmî skor diye sunmak",
          ],
        ],
      },
    },
    {
      id: "wilks-ipf-gl",
      title: "Wilks ile IPF GL arasındaki fark",
      paragraphs: [
        "İki sistem aynı amacı hedeflese de farklı veri ve katsayı kullanır. Aynı vücut ağırlığı ve total, Wilks ile IPF GL'de farklı sayılar üretir. Bu puanların sayısal büyüklüğünü doğrudan kıyaslamak, santimetreyi kilogramla kıyaslamaya benzer: ikisi farklı ölçeklerdir.",
        "International Powerlifting Federation 2018 sonrasında kendi formülüne geçti, daha sonra IPF GL katsayılarını yayımladı. Güncel IPF karşılaştırması yapmak istiyorsan IPF GL'yi seç. Wilks; eski yarışma kayıtları, kulüp standartları ve tarihsel kişisel takip için hâlâ kullanılabilir.",
        "Bir sporcu eski dönemde Wilks, yeni dönemde IPF GL takip ederse trend çizgisini kesintisiz tek puan serisi gibi gösteremez. Geçiş tarihini not etmek veya aynı ham vücut ağırlığı ve total verisini iki formülle ayrı raporlamak gerekir.",
      ],
      sourceIds: ["1", "2", "3"],
    },
    {
      id: "skoru-yorumlama",
      title: "Skor neyi gösterir, neyi göstermez?",
      paragraphs: [
        "Wilks skoru vücut ağırlığına göre ölçeklenmiş total performansını özetler. Daha yüksek skor, aynı formül ve benzer koşullarda daha güçlü göreli performansa işaret eder. Bununla birlikte skor yaş, antrenman yaşı, sakatlık, teknik güvenilirlik veya deneme seçimini açıklamaz.",
        "Kendi gelişimini değerlendirirken puanın bileşenlerini ayrı izle. Totalin artmış fakat vücut ağırlığın da yükselmişse skor daha az artabilir. Kilo verirken totalini korursan skor yükselebilir. Bu, daha düşük kilonun her zaman daha iyi olduğu anlamına gelmez; hızlı kilo kaybı performans ve sağlık açısından maliyet yaratabilir.",
        "Sporcuları karşılaştırırken aynı cinsiyet, formül sürümü, ekipman ve hareket standardını kullan. Farklı federasyonların squat derinliği, komut ve ekipman kuralları puanda görünmez. Resmî başarıyı değerlendirirken yarışmanın kendi sonuç ve sıralamasını esas al.",
      ],
      sourceIds: ["1", "4"],
    },
    {
      id: "gelisim-takibi",
      title: "Wilks skorunu gelişim takibinde kullanmak",
      paragraphs: [
        "Tarihsel bir seri oluşturacaksan her kayıt için tarih, vücut ağırlığı, squat, bench press, deadlift ve total değerini sakla. Böylece puan değiştiğinde bunun hangi kaldırıştan veya kilo değişiminden geldiğini görebilirsin. Yalnız skoru kaydetmek ham veriyi kaybettirir.",
        "Antrenman döneminde e1RM totalinden Wilks hesaplamak motivasyon sağlayabilir; fakat set tahminlerindeki hata üç kaldırış birleştirildiğinde büyüyebilir. Aynı hesap yöntemini kullan ve değeri 'tahmini Wilks' diye etiketle. Yarışma günü hakem onaylı sonuçla ayrı kayıt oluştur.",
      ],
      steps: [
        "Aynı günün vücut ağırlığını ve üç kaldırış totalini kaydet.",
        "Wilks formülünü seçtiğini açıkça belirt.",
        "Puanla birlikte squat, bench press ve deadlift değerlerini koru.",
        "Antrenman ile yarışma sonuçlarını ayrı serilerde değerlendir.",
        "Güncel IPF karşılaştırması gerektiğinde aynı veriyi IPF GL ile yeniden hesapla.",
      ],
    },
    {
      id: "wilks-hatalar",
      title: "Sık yapılan hatalar",
      paragraphs: [
        "Formül hesabını araç yapar; fakat bağlam ve giriş hatası sonucu geçersiz kılabilir.",
      ],
      bullets: [
        "Tek kaldırış sonucunu üç kaldırış totali yerine kullanmak.",
        "Farklı günlerdeki en iyi kaldırışları bir yarışma totali gibi birleştirmek.",
        "Pound değerini kilogram alanına yazmak.",
        "Wilks ve IPF GL puanlarını aynı ölçek kabul etmek.",
        "Antrenman E1RM değerini hakem onaylı sonuç gibi sunmak.",
        "Yalnız puanı saklayıp vücut ağırlığı ve kaldırış dağılımını kaybetmek.",
        "Skoru yükseltmek için performans ve sağlık bağlamından bağımsız kilo kesmek.",
      ],
      note: "Powerlifting performansında yerleşik ifade 'total'dir. Bu rehberde 'totalini artırmak' üç kaldırış performansını; 'toplam' ise genel matematiksel işlemi anlatır.",
    },
    {
      id: "uygulama-ornegi",
      title: "Uygulama örneği: aynı skoru farklı hikâyelerle okumak",
      paragraphs: [
        "90 kg vücut ağırlığında 650 kg total yapan bir sporcu için Wilks skoru hesapladığını düşün. Üç ay sonra sporcu 92 kg ve 665 kg totalle yarışıyor. Skor değişimi yalnız 15 kg total artışını değil, iki kilogramlık vücut ağırlığı değişimini de içerir. Bu nedenle 'Wilks yükseldi' demeden önce hangi bileşenin ne kadar değiştiğini gör.",
        "Başka bir senaryoda sporcu 88 kg'a düşüp 645 kg total yapabilir ve benzer veya daha yüksek skor elde edebilir. Bu, performans bağlamına göre başarılı olabilir; fakat kilo kesiminin yorgunluk, başarısız deneme veya sonraki toparlanma üzerindeki etkisi puanda görünmez. Aynı skor iki farklı hazırlık kalitesini saklayabilir.",
        "Hareket dağılımı da önemlidir. Total aynı kalırken squat yükselip bench press düşmüş olabilir. Bir sonraki programı belirlemek için yalnız Wilks'e bakmak zayıf kalır; kaldırış bazında hacim, yoğunluk ve teknik sorunlar ayrıca incelenmelidir.",
        "Tarihsel karşılaştırmada eski yarışmanın Wilks, yeni yarışmanın IPF GL puanını tek grafik üzerinde birleştirme. Ham verileri koruduysan iki yarışı aynı formülle yeniden hesaplayabilir veya iki puan serisini ayrı tutabilirsin. Böylece formül değişimini gerçek performans değişimi sanmazsın.",
      ],
      sourceIds: ["1", "2"],
    },
    {
      id: "formulun-sinirlari",
      title: "Formülün sınırları ve adil karşılaştırma",
      paragraphs: [
        "Her katsayı formülü geliştirildiği veri dağılımını yansıtır. Çok hafif veya çok ağır sıkletlerde, kadın ve erkek katılımının farklı olduğu dönemlerde modelin karşılaştırma davranışı tartışılabilir. Tek puanın bütün sıkletlerde kusursuz adalet sağlaması beklenmemelidir.",
        "Bir formülün doğrulanması, her bireyde kesin performans sıralaması verdiği anlamına gelmez. Grup düzeyindeki uyum, iki yakın sporcu arasında oluşan küçük puan farkının mutlak üstünlük kanıtı değildir. Özellikle birkaç ondalık farkı; hakem kararı, tartı hassasiyeti ve deneme stratejisinden bağımsız yorumlama.",
        "Kadın ve erkek katsayıları ayrı olduğu için puanlar ilgili formül içinde hesaplanır. Ekipman kategorisi ise klasik Wilks uygulamasında IPF GL'deki gibi ayrı katsayı seçimiyle temsil edilmeyebilir. Raw ve equipped sonuçlarını aynı yarışma standardı gibi karşılaştırmadan önce organizasyonun kullandığı kurala bak.",
      ],
      subsections: [
        {
          title: "Wilks 2 ve sürüm karmaşası",
          paragraphs: [
            "İnternette 'Wilks', 'Wilks 2' veya güncellenmiş katsayı adlarıyla farklı hesaplar görebilirsin. Sonucun hangi katsayı sürümünden geldiği yazmıyorsa başka sitedeki puanla doğrudan karşılaştırma. Bu hesaplayıcıda kullanılan klasik katsayıları ve kaynak sayfasını referans al.",
            "Bir kulüp standardı belirlerken formül adını ve sürümünü belgeye ekle. Aksi hâlde katsayı güncellemesi, sporcu performansı değişmediği hâlde barajı veya tarihsel rekoru değiştirebilir.",
          ],
        },
        {
          title: "Daha adil rapor nasıl hazırlanır?",
          paragraphs: [
            "Puanın yanında sıklet, yaş kategorisi, ekipman, federasyon, yarışma tarihi ve üç kaldırışı göster. Bu bilgiler okuyucunun karşılaştırmanın gerçekten benzer koşullarda olup olmadığını anlamasını sağlar. Mümkünse resmî sonuç bağlantısını da sakla.",
            "Kişisel gelişimde rakip sıralamasından çok aynı formülle hesaplanmış kendi dönemlerini izle. Puanın sabit kaldığı bir dönemde zayıf kaldırışın gelişmiş veya sakatlık sonrası yarışma güvenilirliğin artmış olabilir; bunlar tek skorda görünmez.",
          ],
        },
      ],
      sourceIds: ["1", "4"],
    },
  ],
  faqs: [
    {
      question: "Wilks ve IPF GL aynı mı?",
      answer:
        "Hayır. Farklı katsayı ve sonuç ölçeklerine sahiptirler. Güncel IPF bağlamında IPF GL kullanılır.",
    },
    {
      question: "Wilks hesabında total nedir?",
      answer:
        "En iyi geçerli squat, bench press ve deadlift sonuçlarının kilogram cinsinden toplamıdır.",
    },
    {
      question: "Tek kaldırışla Wilks hesaplanabilir mi?",
      answer:
        "Bu araç üç kaldırış totaline göre tasarlanmıştır. Tek kaldırış yarışmalarında organizasyonun kendi puanlama kuralını kontrol et.",
    },
    {
      question: "Antrenman totalimi girebilir miyim?",
      answer:
        "Kişisel takip için evet; fakat sonucu tahmini/antrenman Wilks olarak etiketle. Resmî skor yalnız yarışma sonucundan gelir.",
    },
    {
      question: "Wilks skorumu nasıl yükseltirim?",
      answer:
        "Temel yol, uygun vücut ağırlığında squat, bench press ve deadlift totalini geliştirmektir. Yalnız kilo düşürmek her zaman puanı veya performansı artırmaz.",
    },
    {
      question: "Wilks skoru yaşa göre düzeltilir mi?",
      answer:
        "Temel Wilks hesabı yaş kullanmaz. Masters puanlaması veya yaş katsayısı varsa organizasyonun ayrıca belirlediği kuraldır.",
    },
    {
      question: "Hangi Wilks değeri iyidir?",
      answer:
        "Tek bir evrensel eşik yoktur. Federasyon, ekipman, cinsiyet ve yarışma düzeyi bağlamı gerekir. En güvenilir kişisel karşılaştırma kendi aynı-standardındaki geçmiş sonuçlarındır.",
    },
    {
      question: "Hesaplayıcı resmî sonuç verir mi?",
      answer:
        "Hayır. Matematiksel tahmin sunar; resmî derece için yarışma sonuçlarını ve ilgili federasyon kurallarını kullan.",
    },
    {
      question: "Klasik Wilks ile Wilks 2 aynı mı?",
      answer:
        "Hayır. Katsayı ve ölçekleri farklı olabilir. Sonucu paylaşırken hangi sürümü kullandığını belirt; farklı sürümlerdeki puanları tek tarihsel seri gibi karşılaştırma.",
    },
    {
      question: "Equipped ve raw sonuçlar aynı Wilks ile karşılaştırılır mı?",
      answer:
        "Bazı tarihsel tablolar aynı katsayıyı kullanmış olsa da ekipman performans dağılımını değiştirir. Adil yorum için aynı ekipman kategorisini ve organizasyon standardını karşılaştır; gerekirse kategoriye özgü sistemi kullan.",
    },
    {
      question: "Başarısız deneme Wilks totaline eklenir mi?",
      answer:
        "Hayır. Yarışma totalinde her kaldırışın en iyi geçerli denemesi kullanılır. Başarısız üçüncü hak, kaldırılan yüke yaklaşmış olsa bile resmî totale dahil değildir.",
    },
    {
      question: "Wilks puanım düştüyse geriledim mi?",
      answer:
        "Tek başına bu sonuç yeterli değildir. Vücut ağırlığı, yarışma totalinin kaldırış dağılımı, sakatlık ve deneme stratejisi puanı etkiler. Ham performans ve birkaç yarışmalık eğilimi birlikte değerlendir.",
    },
    {
      question: "Yakın Wilks puanları kesin sıralama gösterir mi?",
      answer:
        "Hayır. Birkaç ondalık fark model ve ölçüm hassasiyetinin içinde olabilir. Resmî yarışmada sıralama organizasyon kuralına göre belirlenir; genel sporcu kalitesini tek ondalıkla sınıflandırma.",
    },
    {
      question: "Wilks hesabında hangi vücut ağırlığı kullanılmalı?",
      answer:
        "Yarışma sonucu için resmî tartı ağırlığını kullan. Antrenman takibinde total veya E1RM değerinin üretildiği güne yakın, benzer koşullardaki kiloyu gir. Eski en düşük kiloyla yeni en yüksek totali birleştirmek gerçek bir performansı temsil etmez.",
    },
    {
      question: "Wilks skorunu program yüzdesi olarak kullanabilir miyim?",
      answer:
        "Hayır. Wilks, vücut ağırlığına göre total karşılaştırma puanıdır; hareket bazında antrenman yükü vermez. Squat, bench press ve deadlift yüzdeleri için ilgili hareketin 1RM/E1RM değeri ve programın RPE hedefi kullanılmalıdır.",
    },
  ],
  sources: [
    {
      id: "1",
      label: "Powerlifting Australia — Wilks Formula",
      href: "https://powerliftingaustralia.com/wilks-formula/",
    },
    {
      id: "2",
      label: "IPF — Wilks formülünden IPF formülüne geçiş",
      href: "https://www.powerlifting.sport/about-ipf/news/news-detail/ipf-formula",
    },
    {
      id: "3",
      label: "IPF — resmî IPF GL katsayıları",
      href: "https://www.powerlifting.sport/fileadmin/ipf/data/ipf-formula/IPF_GL_Coefficients-2020.pdf",
    },
    {
      id: "4",
      label: "IPF Technical Rules Book 2026",
      href: "https://www.powerlifting.sport/fileadmin/ipf/data/rules/technical-rules/english/IPF_Technical_Rules_Book_2026_1_January_2026.pdf",
    },
  ],
  related: [
    {
      href: "/essay/ipf-gl-calculator",
      label: "IPF GL puanını hesapla",
      description: "Aynı totali güncel IPF katsayılarıyla karşılaştır.",
    },
    {
      href: "/essay/1rm-calculator",
      label: "1RM değerini hesapla",
      description: "Çalışma setinden tahmini maksimumunu bul.",
    },
  ],
  primaryCta: {
    href: "/program",
    label: "Powerlifting programlarını incele",
    description:
      "Squat, bench press ve deadlift totalini sistemli biçimde geliştir.",
  },
};
